import { Box, Text, VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import { useAtomValue } from 'jotai'
import React from 'react'

import { MessageGroup } from '@mangovoice/types'

import { messagingGroupsAtom, smsEligiblePhoneNumbersAtom } from '../../atoms'
import {
  DEFAULT_SMS_MESSAGE_TYPE,
  getSmsTemplateForMessageType,
  SMS_MESSAGE_TEMPLATES,
  SMS_MESSAGE_TYPES,
} from '../../constants/smsNotification'
import { SmsNotificationNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import {
  findMessagingGroupBySendFrom,
  findPhoneNumberOptionBySendFrom,
  getAvailablePhoneNumberOptions,
  getEligibleMessagingGroups,
  getFirstSendFromOptionValue,
} from '../../utils/smsSendFromOptions'
import { text } from '../../utils/text'
import {
  InfoIconTooltip,
  NodeCheckbox,
  NodeInput,
  NodeSelect,
  NodeTextarea,
} from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useSmsNotificationNode } from './hooks/useSmsNotificationNode'

export interface SmsNotificationNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: messagingGroups is provided via Jotai atoms (messagingGroupsAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

const SmsNotificationNodeComponent: React.FC<SmsNotificationNodeProps> = ({
  data,
  id,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  // Read from Jotai atom
  const messagingGroups = useAtomValue(messagingGroupsAtom)
  const smsEligiblePhoneNumbers = useAtomValue(smsEligiblePhoneNumbersAtom)

  const nodeData = data as SmsNotificationNodeData & Record<string, unknown>
  const {
    message,
    phoneNumber,
    handleMessageTypeChange,
    handleMessageChange,
    handleMessageBlur,
    handlePhoneNumberChange,
    handlePhoneNumberBlur,
    handleSendFromChange,
    handleAutoReplyChange,
    currentAutoReplyToCaller,
    currentMinimized,
    handleMinimizedChange,
    messageError,
    phoneNumberError,
    hasData,
  } = useSmsNotificationNode(id, nodeData)

  const t = useSwitchboardT()
  // Keep English values for data; display labels are translated in the dropdown
  const messageTypes = SMS_MESSAGE_TYPES
  const messageTypeLabels: Record<string, string> = {
    'Missed call': text(
      t,
      'switchboard.nodes.smsNotification.missedCall',
      'Missed call',
    ),
    'Incoming call': text(
      t,
      'switchboard.nodes.smsNotification.incomingCall',
      'Incoming call',
    ),
    Voicemail: text(
      t,
      'switchboard.nodes.smsNotification.voicemail',
      'Voicemail',
    ),
    Custom: text(t, 'switchboard.nodes.smsNotification.custom', 'Custom'),
  }

  // Track if we've already auto-populated the message to avoid re-running
  const hasAutoPopulatedRef = React.useRef(false)

  // Auto-populate message when node is created with default message type (matches legacy behavior)
  // This ensures that when a message type is selected (including default "Missed call"),
  // the message textarea is automatically populated with the template
  React.useEffect(() => {
    // Only run once on mount
    if (hasAutoPopulatedRef.current) return

    const messageType = nodeData.messageType as string | undefined
    const resolvedMessageType = messageType || DEFAULT_SMS_MESSAGE_TYPE
    const nodeMessage = (nodeData.message as string | undefined) || ''

    // If message type is set but message is empty in node data, populate it with the template
    // This matches legacy behavior where selecting a message type auto-populates the message
    // Check nodeData.message (not local state) to ensure we're checking the actual saved value
    if (resolvedMessageType !== 'Custom' && !nodeMessage.trim()) {
      const template = SMS_MESSAGE_TEMPLATES[resolvedMessageType]
      if (template) {
        hasAutoPopulatedRef.current = true
        // Persist type + template in one update so local/node state stay in sync.
        handleMessageTypeChange(resolvedMessageType, template)
      }
    }
  }, [nodeData.messageType, nodeData.message, handleMessageTypeChange])

  // Auto-detect message type from message content if messageType is not set or doesn't match
  React.useEffect(() => {
    if (nodeData.message) {
      const message = String(nodeData.message)
      const currentMessageType = nodeData.messageType as string | undefined

      // Check if message matches any template
      let detectedType: string | undefined
      if (message === SMS_MESSAGE_TEMPLATES['Voicemail']) {
        detectedType = 'Voicemail'
      } else if (message === SMS_MESSAGE_TEMPLATES['Incoming call']) {
        detectedType = 'Incoming call'
      } else if (message === SMS_MESSAGE_TEMPLATES['Missed call']) {
        detectedType = 'Missed call'
      } else if (message.includes('went to Voicemail')) {
        // Also check for partial match
        detectedType = 'Voicemail'
      } else if (message.includes('Incoming call from')) {
        detectedType = 'Incoming call'
      } else if (message.includes('Missed call from')) {
        detectedType = 'Missed call'
      }

      // Update message type if detected and different from current
      if (detectedType && detectedType !== currentMessageType) {
        handleMessageTypeChange(detectedType)
      }
    }
  }, [nodeData.message, nodeData.messageType]) // Run when message or messageType changes

  // Handle message type change - auto-populate message if not custom
  const handleMessageTypeChangeWithTemplate = (
    messageType: string | undefined,
  ) => {
    const template = getSmsTemplateForMessageType(messageType)

    if (template) {
      // Selecting a pre-configured type must always apply its template text.
      // Apply both fields atomically to avoid stale-overwrite races.
      handleMessageTypeChange(messageType, template)
      return
    }

    if (messageType === 'Custom') {
      // Selecting Custom should clear the template text and let users author free-form content.
      handleMessageTypeChange(messageType, '')
      return
    }

    handleMessageTypeChange(messageType)

    // Preserve message text for non-template/undefined selections.
  }

  // Helper to get the primary SMS phone number from a messaging group
  const getPrimarySmsPhoneNumber = React.useCallback(
    (group: MessageGroup): string => {
      if (!group.phone_numbers || group.phone_numbers.length === 0) {
        return ''
      }

      // First, try to find the primary SMS phone number
      const primarySms = group.phone_numbers.find(
        (phone) => phone.is_primary_sms === true,
      )
      if (primarySms) {
        return primarySms.number || primarySms.number_for_display || ''
      }

      // Fall back to the first phone number
      const firstPhone = group.phone_numbers[0]
      return firstPhone?.number || firstPhone?.number_for_display || ''
    },
    [],
  )

  const eligibleMessagingGroups = React.useMemo(() => {
    return getEligibleMessagingGroups(messagingGroups, smsEligiblePhoneNumbers)
  }, [messagingGroups, smsEligiblePhoneNumbers])

  // Helper to format messaging group display name
  const formatMessagingGroupDisplay = React.useCallback(
    (group: MessageGroup): string => {
      const phoneNumber = getPrimarySmsPhoneNumber(group)
      const groupName = group.name || ''
      if (phoneNumber && groupName) {
        return `${phoneNumber} (${groupName})`
      }
      if (phoneNumber) {
        return phoneNumber
      }
      if (groupName) {
        return groupName
      }
      return String(group.id)
    },
    [getPrimarySmsPhoneNumber],
  )

  // Get the currently selected messaging group
  const selectedMessagingGroup = React.useMemo(() => {
    return findMessagingGroupBySendFrom(
      eligibleMessagingGroups,
      nodeData.sendFrom as string | undefined,
    )
  }, [eligibleMessagingGroups, nodeData.sendFrom])

  const availablePhoneNumberOptions = React.useMemo(() => {
    return getAvailablePhoneNumberOptions(
      eligibleMessagingGroups,
      smsEligiblePhoneNumbers,
    )
  }, [eligibleMessagingGroups, smsEligiblePhoneNumbers])

  const selectedPhoneNumberOption = React.useMemo(() => {
    return findPhoneNumberOptionBySendFrom(
      availablePhoneNumberOptions,
      nodeData.sendFrom as string | undefined,
    )
  }, [nodeData.sendFrom, availablePhoneNumberOptions])

  const firstSendFromOptionValue = React.useMemo(() => {
    return getFirstSendFromOptionValue(
      eligibleMessagingGroups,
      availablePhoneNumberOptions,
    )
  }, [eligibleMessagingGroups, availablePhoneNumberOptions])

  const hasOrphanSendFromValue = React.useMemo(() => {
    const sendFrom = nodeData.sendFrom as string | undefined
    if (!sendFrom) return false

    return !selectedMessagingGroup && !selectedPhoneNumberOption
  }, [nodeData.sendFrom, selectedMessagingGroup, selectedPhoneNumberOption])

  // Auto-convert phone number to group ID if sendFrom is a phone number (legacy data)
  React.useEffect(() => {
    if (
      nodeData.sendFrom &&
      !selectedMessagingGroup &&
      eligibleMessagingGroups.length > 0
    ) {
      // sendFrom might be a phone number from legacy data, try to find matching group
      const foundGroup = findMessagingGroupBySendFrom(
        eligibleMessagingGroups,
        nodeData.sendFrom,
      )
      if (foundGroup) {
        // Update sendFrom to use group ID instead of phone number
        handleSendFromChange(String(foundGroup.id))
      }
    }
  }, [
    nodeData.sendFrom,
    selectedMessagingGroup,
    eligibleMessagingGroups,
    handleSendFromChange,
  ])

  // Match legacy: when there is no value yet, auto-select the first available "Send from" option.
  // This and the legacy-conversion effect above are intentionally separated and
  // mutually exclusive:
  // - conversion effect requires truthy `sendFrom`
  // - auto-select effect requires falsy `sendFrom`
  React.useEffect(() => {
    const sendFrom = nodeData.sendFrom as string | undefined
    if (!sendFrom && firstSendFromOptionValue) {
      handleSendFromChange(firstSendFromOptionValue)
    }
  }, [nodeData.sendFrom, firstSendFromOptionValue, handleSendFromChange])

  const renderMessageTypeSelect = () => (
    <NodeSelect
      label={text(
        t,
        'switchboard.nodes.smsNotification.messageType',
        'Message Type',
      )}
      value={nodeData.messageType || DEFAULT_SMS_MESSAGE_TYPE}
      onChange={(e) =>
        handleMessageTypeChangeWithTemplate(e.target.value || undefined)
      }
    >
      {messageTypes.map((type) => (
        <option key={type} value={type}>
          {messageTypeLabels[type] ?? type}
        </option>
      ))}
    </NodeSelect>
  )

  const resolvedMessageType =
    (nodeData.messageType as string | undefined) || DEFAULT_SMS_MESSAGE_TYPE
  const messagePlaceholder =
    resolvedMessageType === 'Custom'
      ? text(
          t,
          'switchboard.nodes.smsNotification.customMessagePlaceholder',
          'Enter a custom SMS message. You can use {CALLER_ID} and {CALLER_NUMBER}.',
        )
      : text(
          t,
          'switchboard.nodes.smsNotification.messagePlaceholder',
          'Missed call from {CALLER_ID}-{CALLER_NUMBER}',
        )

  const renderSmsRecipientInput = () => (
    <NodeInput
      label={text(
        t,
        'switchboard.nodes.smsNotification.smsRecipient',
        'SMS Recipient',
      )}
      value={phoneNumber}
      onChange={(e) => handlePhoneNumberChange(e.target.value)}
      onBlur={handlePhoneNumberBlur}
      placeholder="5550001234"
      required={!currentAutoReplyToCaller}
      error={phoneNumberError || undefined}
      disabled={currentAutoReplyToCaller}
    />
  )

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.sms_notification',
        'SMS Notification',
      )}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      minimized={currentMinimized}
      onMinimizeToggle={() => handleMinimizedChange(!currentMinimized)}
      minimizedContent={renderMessageTypeSelect()}
    >
      <VStack align="stretch" gap={2}>
        {renderMessageTypeSelect()}

        <Box position="relative">
          <NodeTextarea
            label={text(
              t,
              'switchboard.nodes.smsNotification.message',
              'Message',
            )}
            value={message}
            onChange={(e) => handleMessageChange(e.target.value)}
            onBlur={handleMessageBlur}
            placeholder={messagePlaceholder}
            rows={3}
            required
            error={messageError || undefined}
            maxLength={1000}
            showCharCount
          />
          <Box
            position="absolute"
            top="-10px"
            left="-8px"
            zIndex={100}
            pointerEvents="none"
          >
            <Box pointerEvents="auto">
              <InfoIconTooltip
                title={text(
                  t,
                  'switchboard.nodes.smsNotification.tooltipNotesTitle',
                  'Notes',
                )}
                ariaLabel={text(
                  t,
                  'switchboard.nodes.smsNotification.tooltipNotesAria',
                  'SMS Message information',
                )}
                iconSize="small"
                content={
                  <VStack align="stretch" gap={2}>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      {text(
                        t,
                        'switchboard.nodes.smsNotification.tooltip.intro',
                        'Create your own message, embed Mango values. For example: An incoming call from 888-555-1212 with a Caller ID of John Doe would be entered as',
                      )}
                    </Text>
                    <Text
                      as="code"
                      fontSize="xs"
                      bg="bg.subtle"
                      p={2}
                      display="block"
                      borderRadius="sm"
                      wordBreak="break-word"
                      overflowWrap="break-word"
                      maxW="100%"
                      style={{ wordWrap: 'break-word' }}
                    >
                      {text(
                        t,
                        'switchboard.nodes.smsNotification.tooltip.exampleTemplate',
                        'Missed call from {CALLER_ID}-{CALLER_NUMBER}',
                      )}
                    </Text>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      {text(
                        t,
                        'switchboard.nodes.smsNotification.tooltip.exampleResultLeadIn',
                        'This would send a SMS message to the SMS Recipient that would appear like this',
                      )}
                    </Text>
                    <Text
                      as="code"
                      fontSize="xs"
                      bg="bg.subtle"
                      p={2}
                      display="block"
                      borderRadius="sm"
                      wordBreak="break-word"
                      overflowWrap="break-word"
                      maxW="100%"
                      style={{ wordWrap: 'break-word' }}
                    >
                      {text(
                        t,
                        'switchboard.nodes.smsNotification.tooltip.exampleResult',
                        'Missed call from John Doe-8885551212',
                      )}
                    </Text>
                    <Box>
                      <Text fontWeight="semibold" mb={1}>
                        {text(
                          t,
                          'switchboard.nodes.common.tooltip.variablesHeading',
                          'Variables',
                        )}
                      </Text>
                      <VStack align="stretch" gap={1} fontSize="xs">
                        <Text wordBreak="break-word" overflowWrap="break-word">
                          <Text as="strong">CALLER_ID</Text> -{' '}
                          {text(
                            t,
                            'switchboard.nodes.common.tooltip.callerIdDesc',
                            'The caller id name of the person calling',
                          )}
                        </Text>
                        <Text wordBreak="break-word" overflowWrap="break-word">
                          <Text as="strong">CALLER_NUMBER</Text> -{' '}
                          {text(
                            t,
                            'switchboard.nodes.common.tooltip.callerNumberDesc',
                            'The number of the person calling',
                          )}
                        </Text>
                        <Text wordBreak="break-word" overflowWrap="break-word">
                          <Text as="strong">NUMBER_DIALED</Text> -{' '}
                          {text(
                            t,
                            'switchboard.nodes.common.tooltip.numberDialedDesc',
                            'The number that was dialed',
                          )}
                        </Text>
                        <Text wordBreak="break-word" overflowWrap="break-word">
                          <Text as="strong">CALL_UID</Text> -{' '}
                          {text(
                            t,
                            'switchboard.nodes.common.tooltip.callUidDesc',
                            'The unique id of the call that can be used to lookup the call record at a later time.',
                          )}
                        </Text>
                        <Text wordBreak="break-word" overflowWrap="break-word">
                          <Text as="strong">DIALED_NUMBER_NAME</Text> -{' '}
                          {text(
                            t,
                            'switchboard.nodes.common.tooltip.dialedNumberNameDesc',
                            'The name in mango of the number being called',
                          )}
                        </Text>
                        <Text wordBreak="break-word" overflowWrap="break-word">
                          <Text as="strong">TIME_UTC</Text> -{' '}
                          {text(
                            t,
                            'switchboard.nodes.common.tooltip.timeUtcDesc',
                            'The time of the Call in UTC time zone',
                          )}
                        </Text>
                      </VStack>
                    </Box>
                  </VStack>
                }
              />
            </Box>
          </Box>
        </Box>

        <Box>
          <Box position="relative">
            {renderSmsRecipientInput()}
            <Box
              position="absolute"
              top="-10px"
              left="-8px"
              zIndex={100}
              pointerEvents="none"
            >
              <Box pointerEvents="auto">
                <InfoIconTooltip
                  title={text(
                    t,
                    'switchboard.nodes.smsNotification.smsRecipient',
                    'SMS Recipient',
                  )}
                  ariaLabel={text(
                    t,
                    'switchboard.nodes.smsNotification.smsRecipientInfo',
                    'SMS Recipient information',
                  )}
                  iconSize="small"
                  content={
                    <Text>
                      {text(
                        t,
                        'switchboard.nodes.smsNotification.separateNumbersHint',
                        'Separate numbers with a comma to send to multiple recipients',
                      )}
                    </Text>
                  }
                />
              </Box>
            </Box>
          </Box>

          {/* OR Separator */}
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            my={2}
            color="fg.muted"
            fontSize="sm"
            fontWeight="semibold"
          >
            <Box flex={1} height="1px" bg="border.default" />
            <Text as="span" whiteSpace="nowrap">
              {text(t, 'switchboard.nodes.smsNotification.or', 'OR')}
            </Text>
            <Box flex={1} height="1px" bg="border.default" />
          </Box>

          <Box mt={1}>
            <NodeCheckbox
              label={text(
                t,
                'switchboard.nodes.smsNotification.autoReplyToCaller',
                'Auto-reply to Caller',
              )}
              checked={currentAutoReplyToCaller}
              onChange={(e) => handleAutoReplyChange(e.target.checked)}
            />
          </Box>
        </Box>

        <Box>
          <NodeSelect
            label={text(
              t,
              'switchboard.nodes.smsNotification.sendFrom',
              'Send from',
            )}
            value={
              selectedMessagingGroup
                ? String(selectedMessagingGroup.id)
                : selectedPhoneNumberOption
                  ? selectedPhoneNumberOption.value
                  : nodeData.sendFrom || firstSendFromOptionValue || ''
            }
            onChange={(e) => {
              const selectedValue = e.target.value
              // Store either messaging group ID or direct phone number (legacy-compatible).
              handleSendFromChange(selectedValue || undefined)
            }}
          >
            {eligibleMessagingGroups.map((group) => {
              const displayName = formatMessagingGroupDisplay(group)
              return (
                <option key={group.id} value={String(group.id)}>
                  {displayName}
                </option>
              )
            })}
            {availablePhoneNumberOptions.map((phone) => (
              <option key={`phone-${phone.value}`} value={phone.value}>
                {phone.label}
              </option>
            ))}
            {hasOrphanSendFromValue && (
              <option value={String(nodeData.sendFrom)}>
                {text(
                  t,
                  'switchboard.nodes.smsNotification.currentUnavailableNumber',
                  'Current value (unavailable): {{value}}',
                  {
                    value: String(nodeData.sendFrom),
                  },
                )}
              </option>
            )}
          </NodeSelect>
          {selectedMessagingGroup && (
            <Box
              as="span"
              fontSize="xs"
              color="fg.muted"
              mt={1}
              display="block"
            >
              {text(
                t,
                'switchboard.nodes.smsNotification.smsGroupLabel',
                'SMS Group:',
              )}{' '}
              {selectedMessagingGroup.name}
            </Box>
          )}
        </Box>
      </VStack>
    </BaseNode>
  )
}

export const SmsNotificationNode = React.memo(
  SmsNotificationNodeComponent,
  nodePropsAreEqual,
)
